import { Query } from "@/app/graphql/codegen";
import { FETCH_CONTEXT_SELECTION_FOR_CHAT } from "@chat/graphql/queries/fetchContextSelectionForChat";
import { FETCH_DOCUMENT_TREE } from "@documents/graphql/queries/fetchDocumentTree";
import { selectDocumentFilters } from "@store/slices/documentSelection";
import { gql, useQuery } from "@apollo/client";
import { useSelector } from "react-redux";
import { useMemo } from "react";
import { filterLatestDocuments } from "@utils/documentVersion";


export const useDocumentTree = (chatId?: string) => {
    // Aktuelle Filter/Sortierkriterien aus dem Store
    const filters = useSelector(selectDocumentFilters);

    const {
        data: filteredDocumentData,
        loading: filteredDocumentLoading,
        error: filteredDocumentError,
    } = useQuery<Pick<Query, "filteredDocuments">>(FETCH_DOCUMENT_TREE, {
        variables: {
            filter: {
                searchTerm: filters.searchTerm,
                type: filters.type,
            },
            sort: {
                field: filters.sortField,
                order: filters.sortOrder,
            },
            chatMode: !!chatId,
        },
    });

    const { 
      data: contextData,
      loading: contextLoading,
      error: contextError
    } = useQuery<Pick<Query, "contextForChat">>(FETCH_CONTEXT_SELECTION_FOR_CHAT, {
        variables: { chatId },
        skip: !chatId
    });

    const baseDocsAll = filteredDocumentData?.filteredDocuments || [];

    // Filter to latest version per reference
    const baseDocs = useMemo(() => filterLatestDocuments(baseDocsAll), [baseDocsAll]);

    const documents = useMemo(() => {
        if (filters.jurisdiction && filters.jurisdiction !== "all") {
            return baseDocs.filter((d: any) => {
                const jur = d.jurisdiction || d.metadata?.jurisdiction;
                // Normalize: treat "DE" and "GER" as equivalent
                const normalizedJur = jur === "GER" ? "DE" : jur;
                const normalizedFilter = filters.jurisdiction === "GER" ? "DE" : filters.jurisdiction;
                return normalizedJur === normalizedFilter;
            });
        }
        return baseDocs;
    }, [baseDocs, filters.jurisdiction]);

    return {
      documents,
      context: contextData?.contextForChat || null,
      loading: filteredDocumentLoading || contextLoading,
      error: filteredDocumentError || contextError,
    };

};
